// bb-home-colecao-destaque.js -- extraído de featured-collection.liquid (Lote 5, Task 9).
// Carrossel nativo à mão (achado #41): drag por mousedown, dots criados com
// createElement, clones marcados com [data-rgd-clone]. Não é reescrito aqui -- só sai
// do <script> inline e vira asset. A unificação com o carrossel gêmeo de
// rgd-image-logo-slider (Task 10) fica registrada no fechamento do lote, porque juntar
// os dois é redesenho, não extração.
//
// O que era {{ section.id }}/{{ section.settings.x }} no HTML agora é lido do DOM: o
// escopo de cada instância é o ancestral .bb-colecao-destaque (múltiplas instâncias da
// seção na mesma página não colidem), e os números de settings vêm de data-attributes
// na mesma tag. Preserva o comportamento de hoje, achado incluído: o bloco só inicializa
// quando há abas ([data-rgd-tabs] existe só dentro do ramo has_tabs); a seção "coleção
// única" sem abas não tem esse carrossel ligado -- não é consertado aqui, fora do escopo
// da Task 9.

(() => {
  // Um <script src> por instância da seção: com N instâncias esta IIFE roda N vezes. Só a
  // primeira registra os listeners de document e inicializa (ela já vê todas as instâncias,
  // porque o script é defer); as outras sairiam com N listeners de load/unload repetidos.
  // Mesmo padrão de bb-swiper.js.
  if (window.bbColecaoDestaque) return;
  window.bbColecaoDestaque = true;

  // Os listeners abaixo em document/window sobrevivem à seção: o editor de temas troca
  // o HTML da seção sem recarregar a página. Cada seção guarda os que registrou e os
  // devolve no unload, para não acumular a cada edição.
  const registrados = new Map(); // secaoRaiz -> [[alvo, evento, handler], ...]

  const registrar = (secaoRaiz, alvo, evento, handler, opcoes) => {
    alvo.addEventListener(evento, handler, opcoes);
    if (!registrados.has(secaoRaiz)) registrados.set(secaoRaiz, []);
    registrados.get(secaoRaiz).push([alvo, evento, handler]);
  };

  document.addEventListener('shopify:section:unload', (event) => {
    (registrados.get(event.target) || []).forEach(([alvo, evento, handler]) => alvo.removeEventListener(evento, handler));
    registrados.delete(event.target);
  });

  const initTabs = (section) => {
    if (section.dataset.rgdTabsReady === 'true') return;

    // Sem a raiz não há de onde ler data-cards-per-view-*: pula esta e segue para as
    // outras instâncias (cair em document dava TypeError e abortava o forEach).
    const collectionRoot = section.closest('.bb-colecao-destaque');
    if (!collectionRoot) {
      console.warn('bb-home-colecao-destaque: [data-rgd-tabs] fora de .bb-colecao-destaque, ignorado', section);
      return;
    }

    // Sem o wrapper da seção não há chave para os listeners de document/window: cair em
    // document os juntaria sob uma chave só, e o unload de outra seção nunca os acharia.
    const secaoRaiz = section.closest('[id^="shopify-section-"]');
    if (!secaoRaiz) {
      console.warn('bb-home-colecao-destaque: [data-rgd-tabs] fora de [id^="shopify-section-"], ignorado', section);
      return;
    }

    section.dataset.rgdTabsReady = 'true';

    const cardsPerViewDesktop = Number(collectionRoot.dataset.cardsPerViewDesktop) || 1;
    const cardsPerViewMobile = Number(collectionRoot.dataset.cardsPerViewMobile) || 1;

    const buttons = Array.from(collectionRoot.querySelectorAll('[data-rgd-tab-button]'));
    const panels = Array.from(collectionRoot.querySelectorAll('[data-rgd-tab-panel]'));

    const activateTab = (button) => {
      const panelId = button.getAttribute('aria-controls');

      buttons.forEach((item) => {
        const isCurrent = item === button;
        item.classList.toggle('is-active', isCurrent);
        item.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        item.setAttribute('tabindex', isCurrent ? '0' : '-1');
      });

      panels.forEach((panel) => {
        const isCurrent = panel.id === panelId;
        panel.classList.toggle('is-active', isCurrent);
        panel.hidden = !isCurrent;
      });

      const activePanel = document.getElementById(panelId);
      const activeSlider = activePanel ? activePanel.querySelector('.product-grid') : null;

      if (activeSlider) {
        activeSlider.dataset.rgdSwitching = 'true';

        requestAnimationFrame(() => {
          const dotsWrap = collectionRoot.querySelector('[data-rgd-carousel-dots][data-rgd-target="' + activeSlider.id + '"]');

          if (typeof prepareInfiniteLoop === 'function') {
            prepareInfiniteLoop(activeSlider, true);
          }

          if (dotsWrap && typeof buildDots === 'function') {
            buildDots(activeSlider, dotsWrap);
          }

          if (typeof resetToLoopStart === 'function') {
            resetToLoopStart(activeSlider);
          }

          requestAnimationFrame(() => {
            activeSlider.dataset.rgdSwitching = 'false';
          });
        });
      }

      window.dispatchEvent(new Event('resize'));
    };

    buttons.forEach((button, index) => {
      button.setAttribute('tabindex', button.classList.contains('is-active') ? '0' : '-1');

      button.addEventListener('click', () => activateTab(button));

      button.addEventListener('keydown', (event) => {
        const key = event.key;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

        event.preventDefault();

        let nextIndex = index;

        if (key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
        if (key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
        if (key === 'Home') nextIndex = 0;
        if (key === 'End') nextIndex = buttons.length - 1;

        buttons[nextIndex].focus();
        activateTab(buttons[nextIndex]);
      });
    });

    const isDesktop = () => {
      return window.matchMedia('(min-width: 1125px)').matches;
    };

    const getCardsPerView = () => {
      return isDesktop() ? cardsPerViewDesktop : cardsPerViewMobile;
    };

    const isSliderVisible = (slider) => {
      return !!(slider && slider.offsetParent !== null && slider.clientWidth > 0);
    };

    const getGap = (slider) => {
      const styles = window.getComputedStyle(slider);
      return parseFloat(styles.columnGap || styles.gap || 0) || 0;
    };

    const getRealItems = (slider) => {
      return Array.from(slider.children).filter((item) => {
        return item.classList.contains('grid__item') && item.dataset.rgdClone !== 'true' && window.getComputedStyle(item).display !== 'none';
      });
    };

    const removeClones = (slider) => {
      Array.from(slider.querySelectorAll('[data-rgd-clone="true"]')).forEach((clone) => clone.remove());

      slider.dataset.rgdLoopReady = 'false';
      slider.dataset.rgdOriginalCount = '';
      slider.dataset.rgdCloneCount = '';
    };

    const getItemStep = (slider) => {
      const firstItem = getRealItems(slider)[0];
      if (!firstItem) return 0;

      return firstItem.getBoundingClientRect().width + getGap(slider);
    };

    const getPageStep = (slider) => {
      return getItemStep(slider) * getCardsPerView();
    };

    const getPageCount = (slider) => {
      const total = getRealItems(slider).length;
      const perView = getCardsPerView();

      if (!total || total <= perView) return 0;

      return Math.ceil(total / perView);
    };

    const resetToLoopStart = (slider) => {
      if (!isSliderVisible(slider)) return;

      if (!isDesktop()) {
        slider.scrollTo({
          left: 0,
          behavior: 'auto'
        });
        return;
      }

      const cloneCount = Number(slider.dataset.rgdCloneCount || 0);
      const step = getItemStep(slider);

      if (cloneCount && step) {
        slider.scrollTo({
          left: cloneCount * step,
          behavior: 'auto'
        });
      } else {
        slider.scrollTo({
          left: 0,
          behavior: 'auto'
        });
      }
    };

    const normalizeInfinitePosition = (slider) => {
      if (!isDesktop()) return;
      if (!isSliderVisible(slider)) return;
      if (slider.dataset.rgdLoopReady !== 'true') return;
      if (slider.dataset.rgdSwitching === 'true') return;

      const originalCount = Number(slider.dataset.rgdOriginalCount || 0);
      const cloneCount = Number(slider.dataset.rgdCloneCount || 0);
      const step = getItemStep(slider);

      if (!originalCount || !cloneCount || !step) return;

      const start = cloneCount * step;
      const end = (cloneCount + originalCount) * step;
      const current = slider.scrollLeft;

      if (current < start - step) {
        slider.scrollLeft = current + originalCount * step;
      } else if (current >= end) {
        slider.scrollLeft = current - originalCount * step;
      }
    };

    const updateDots = (slider, dotsWrap) => {
      if (!isSliderVisible(slider)) return;

      const dots = Array.from(dotsWrap.querySelectorAll('[data-rgd-carousel-dot]'));
      if (!dots.length) return;

      const step = getItemStep(slider);
      const pageStep = getPageStep(slider);
      if (!step || !pageStep) return;

      let logicalLeft = slider.scrollLeft;

      if (isDesktop()) {
        const originalCount = Number(slider.dataset.rgdOriginalCount || getRealItems(slider).length);
        const cloneCount = Number(slider.dataset.rgdCloneCount || 0);

        if (originalCount && cloneCount) {
          const fullWidth = originalCount * step;
          logicalLeft = slider.scrollLeft - (cloneCount * step);
          logicalLeft = ((logicalLeft % fullWidth) + fullWidth) % fullWidth;
        }
      }

      const activeIndex = Math.min(
        Math.round(logicalLeft / pageStep),
        dots.length - 1
      );

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const buildDots = (slider, dotsWrap) => {
      const pageCount = getPageCount(slider);

      dotsWrap.innerHTML = '';

      if (pageCount <= 1) {
        dotsWrap.hidden = true;
        return;
      }

      dotsWrap.hidden = false;

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = `rgd-carousel-dot${index === 0 ? ' is-active' : ''}`;
        dot.dataset.rgdCarouselDot = '';
        dot.dataset.rgdIndex = String(index);
        dot.setAttribute('aria-label', `Ir para página ${index + 1}`);
        dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');

        dot.addEventListener('click', () => {
          if (!isSliderVisible(slider)) return;

          const pageStep = getPageStep(slider);
          let left = pageStep * index;

          if (isDesktop()) {
            const cloneCount = Number(slider.dataset.rgdCloneCount || 0);
            const step = getItemStep(slider);
            left = cloneCount * step + pageStep * index;
          }

          slider.scrollTo({
            left,
            behavior: 'smooth'
          });

          Array.from(dotsWrap.querySelectorAll('[data-rgd-carousel-dot]')).forEach((item) => {
            item.classList.remove('is-active');
            item.setAttribute('aria-current', 'false');
          });

          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        });

        dotsWrap.appendChild(dot);
      }

      updateDots(slider, dotsWrap);
    };

    const prepareInfiniteLoop = (slider, force = false) => {
      if (!isSliderVisible(slider)) return;

      if (!isDesktop()) {
        removeClones(slider);
        slider.scrollLeft = Math.min(slider.scrollLeft, Math.max(slider.scrollWidth - slider.clientWidth, 0));
        return;
      }

      if (force) {
        removeClones(slider);
      }

      if (slider.dataset.rgdLoopReady === 'true') return;

      const realItems = getRealItems(slider);
      const perView = getCardsPerView();

      if (realItems.length <= perView) {
        slider.dataset.rgdLoopReady = 'false';
        return;
      }

      const cloneCount = Math.min(perView, realItems.length);
      const beforeItems = realItems.slice(-cloneCount);
      const afterItems = realItems.slice(0, cloneCount);

      beforeItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.rgdClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        slider.insertBefore(clone, slider.firstChild);
      });

      afterItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.rgdClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        slider.appendChild(clone);
      });

      slider.dataset.rgdLoopReady = 'true';
      slider.dataset.rgdOriginalCount = String(realItems.length);
      slider.dataset.rgdCloneCount = String(cloneCount);

      requestAnimationFrame(() => resetToLoopStart(slider));
    };

    const initDragScroll = (slider) => {
      if (slider.dataset.rgdDragReady === 'true') return;
      slider.dataset.rgdDragReady = 'true';

      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let moved = false;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      let rafMomentum = null;

      const canDrag = () => {
        return isDesktop() && slider.scrollWidth > slider.clientWidth + 2;
      };

      const stopMomentum = () => {
        if (rafMomentum) {
          cancelAnimationFrame(rafMomentum);
          rafMomentum = null;
        }
      };

      const momentum = () => {
        if (!isSliderVisible(slider) || !isDesktop()) {
          rafMomentum = null;
          return;
        }

        velocity *= 0.9;

        if (Math.abs(velocity) < 0.035) {
          rafMomentum = null;
          normalizeInfinitePosition(slider);
          return;
        }

        slider.scrollLeft -= velocity * 16;
        normalizeInfinitePosition(slider);

        rafMomentum = requestAnimationFrame(momentum);
      };

      const start = (event) => {
        if (!canDrag()) return;
        if (event.button !== 0) return;

        stopMomentum();

        isDragging = true;
        moved = false;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = performance.now();
        startScrollLeft = slider.scrollLeft;
        velocity = 0;

        slider.classList.add('is-dragging');
        document.documentElement.classList.add('rgd-carousel-page-dragging');

        event.preventDefault();
      };

      const move = (event) => {
        if (!isDragging) return;

        const now = performance.now();
        const x = event.clientX;
        const distance = x - startX;
        const deltaX = x - lastX;
        const deltaTime = Math.max(now - lastTime, 16);

        velocity = deltaX / deltaTime;

        if (Math.abs(distance) > 2) moved = true;

        slider.scrollLeft = startScrollLeft - distance;
        normalizeInfinitePosition(slider);

        lastX = x;
        lastTime = now;

        event.preventDefault();
      };

      const endDrag = () => {
        if (!isDragging) return;

        isDragging = false;
        slider.classList.remove('is-dragging');
        document.documentElement.classList.remove('rgd-carousel-page-dragging');

        if (moved) {
          slider.dataset.rgdJustDragged = 'true';

          if (Math.abs(velocity) > 0.08) {
            rafMomentum = requestAnimationFrame(momentum);
          }

          window.setTimeout(() => {
            slider.dataset.rgdJustDragged = 'false';
          }, 150);
        }
      };

      slider.addEventListener('mousedown', start);
      registrar(secaoRaiz, document, 'mousemove', move, { passive: false });
      registrar(secaoRaiz, document, 'mouseup', endDrag);
      registrar(secaoRaiz, window, 'blur', endDrag);

      slider.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });

      slider.addEventListener('click', (event) => {
        if (slider.dataset.rgdJustDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    };

    const initCarouselDots = (force = false) => {
      const dotsGroups = Array.from(collectionRoot.querySelectorAll('[data-rgd-carousel-dots]'));

      dotsGroups.forEach((dotsWrap) => {
        const slider = document.getElementById(dotsWrap.dataset.rgdTarget);
        if (!slider || !isSliderVisible(slider)) return;

        prepareInfiniteLoop(slider, force);
        initDragScroll(slider);
        buildDots(slider, dotsWrap);

        if (dotsWrap.dataset.rgdScrollReady !== 'true') {
          dotsWrap.dataset.rgdScrollReady = 'true';

          slider.addEventListener('scroll', () => {
            if (slider.dataset.rgdSwitching === 'true') return;

            normalizeInfinitePosition(slider);
            window.requestAnimationFrame(() => updateDots(slider, dotsWrap));
          }, { passive: true });
        }
      });
    };

    initCarouselDots();

    registrar(secaoRaiz, window, 'resize', () => {
      window.requestAnimationFrame(() => initCarouselDots(true));
    });
  };

  document.querySelectorAll('[data-rgd-tabs]').forEach(initTabs);

  // Editor de temas: a seção recarregada chega por AJAX e o script não roda de novo
  // ("Integrate sections and blocks with the theme editor"). O unload acima limpa a
  // instância velha; aqui a nova é inicializada.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-rgd-tabs]').forEach(initTabs);
  });
})();
